import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import LinearProgress from '@material-ui/core/LinearProgress';
import Paper from '@material-ui/core/Paper';

import FileDropzone from 'components/FileDropzone';
import { createBulkUpload, getValidContentTypes, validateFileNames } from 'api/report';
import useStyle from '../SingleReportUpload/style';

const makeId = file => `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(16).slice(2)}`;

const preUploadValidation = async filenames => {
  try {
    const resp = await validateFileNames({ filenames });

    // Expected backend response shape from the validator:
    // { valid: [...], invalid: [{ filename, errors: [...] }, ...] }
    const { invalid = [] } = resp.data || {};

    const errorsByName = {};
    invalid.forEach(({ filename, errors }) => {
      // join multiple errors into one string for UI
      errorsByName[filename] = Array.isArray(errors) ? errors.join(' ') : String(errors);
    });

    return { ok: invalid.length === 0, errorsByName };
  } catch (e) {
    const data = e?.response?.data;
    if (data?.invalid) {
      const errorsByName = {};
      data.invalid.forEach(({ filename, errors }) => {
        errorsByName[filename] = Array.isArray(errors) ? errors.join(' ') : String(errors);
      });
      return { ok: false, errorsByName };
    }

    console.error(e);
    return {
      ok: false,
      errorsByName: Object.fromEntries(
        filenames.map(n => [n, 'Could not validate filenames. Please try again.']),
      ),
    };
  }
};

const CONCURRENCY_DEFAULT = 3;
const FILE_LIMIT_DEFAULT = 100;

const BulkReportUpload = () => {
  const classes = useStyle();

  const [showDialog, setShowDialog] = useState(false);

  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const [validContentTypes, setValidContentTypes] = useState('');
  const [isUploadingBatch, setIsUploadingBatch] = useState(false);

  // Upload items state
  // status: selected | validating | invalid | queued | uploading | saving | completed | failed
  const [items, setItems] = useState([]);

  const fileLimit = FILE_LIMIT_DEFAULT;
  const concurrency = CONCURRENCY_DEFAULT;

  const rowRefs = useRef({});

  const setRowRef = useCallback(
    id => el => {
      if (el) rowRefs.current[id] = el;
      else delete rowRefs.current[id];
    },
    [],
  );

  const scrollToItem = useCallback(id => {
    const el = rowRefs.current[id];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // ----------------------------
  // Derived state
  // ----------------------------

  const hasInvalid = useMemo(
    () => items.some(i => i.status === 'invalid'),
    [items],
  );

  const invalidCount = useMemo(
    () => items.filter(i => i.status === 'invalid').length,
    [items],
  );

  const firstInvalid = useMemo(
    () => items.find(i => i.status === 'invalid'),
    [items],
  );

  const queuedCount = useMemo(
    () => items.filter(i => i.status === 'queued').length,
    [items],
  );
  const uploadingOrSavingCount = useMemo(
    () => items.filter(i => i.status === 'uploading' || i.status === 'saving').length,
    [items],
  );
  const completedCount = useMemo(
    () => items.filter(i => i.status === 'completed').length,
    [items],
  );
  const failedCount = useMemo(
    () => items.filter(i => i.status === 'failed').length,
    [items],
  );

  const clearMessages = () => {
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const resetForm = () => {
    setItems([]);
    setShowDialog(false);
    setIsUploadingBatch(false);
    clearMessages();
  };

  const removeItem = id => {
    delete rowRefs.current[id];
    setItems(prev => prev.filter(x => x.id !== id));
  };

  // ---- Validation ----
  const runValidation = useCallback(
    async nextItems => {
      setItems(nextItems.map(it => ({ ...it, status: 'validating', error: null })));

      const { ok, errorsByName } = await preUploadValidation(nextItems.map(it => it.file.name));

      const validated = nextItems.map(it => {
        const err = errorsByName[it.file.name];
        if (err) return { ...it, status: 'invalid', error: err };
        return { ...it, status: 'queued', error: null };
      });

      setItems(validated);

      // UX: scroll to the first invalid file so user sees the error
      const firstBad = validated.find(i => i.status === 'invalid');
      if (firstBad) {
        setTimeout(() => scrollToItem(firstBad.id), 0);
      }

      return ok;
    },
    [scrollToItem],
  );

  const onDrop = async files => {
    clearMessages();

    const capped = files.slice(0, Math.max(0, fileLimit - items.length));
    if (capped.length === 0) return;

    const nextItems = [
      ...items,
      ...capped.map(file => ({
        id: makeId(file),
        file,
        status: 'selected',
        progress: 0,
        error: null,
      })),
    ];

    // Auto-validate immediately
    await runValidation(nextItems);
  };

  // ---- Upload queue runner ----
  const updateItem = useCallback((id, patch) => {
    setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const uploadOne = useCallback(async item => {
    const data = new FormData();
    data.append('report[file]', item.file);

    updateItem(item.id, { status: 'uploading', progress: 0, error: null });

    try {
      const resp = await createBulkUpload(data, {
        onUploadProgress: evt => {
          if (!evt.total) return;
          const pct = Math.round((evt.loaded * 100) / evt.total);
          updateItem(item.id, { progress: Math.min(100, pct) });
        },
      });

      // upload bytes are done; backend is finalizing/persisting metadata
      updateItem(item.id, { status: 'saving', progress: 100 });

      // If createReport only returns after save is done, this state may be brief.
      // Still useful as a conceptual state.
      updateItem(item.id, { status: 'completed', response: resp });

      return { ok: true };
    } catch (e) {
      // `reason` is the key the API's rescue handlers use; without it axios leaves only a bare
      // "Request failed with status code NNN".
      const body = e?.response?.data;
      const msg = body?.message ?? body?.reason ?? e?.message ?? 'Upload failed';
      updateItem(item.id, { status: 'failed', error: msg });
      return { ok: false, error: msg };
    }
  }, [updateItem]);

  // Takes the batch explicitly rather than reading `items`, so a caller that has just re-queued
  // rows can pass its own list instead of racing the pending state update.
  const runQueue = useCallback(async batchItems => {
    const toUpload = batchItems.filter(it => it.status === 'queued');
    if (toUpload.length === 0) return [];

    let idx = 0;
    const results = [];

    const worker = async () => {
      while (idx < toUpload.length) {
        const current = toUpload[idx];
        idx += 1;

        // eslint-disable-next-line no-await-in-loop
        const r = await uploadOne(current);
        results.push({ id: current.id, fileName: current.file.name, ...r });
      }
    };

    await Promise.all(Array.from({ length: concurrency }, () => worker()));
    return results;
  }, [concurrency, uploadOne]);

  const runBatch = useCallback(async batchItems => {
    if (batchItems.filter(it => it.status === 'queued').length === 0) return [];

    setIsUploadingBatch(true);
    setErrorMessage(null);

    const results = await runQueue(batchItems);

    setIsUploadingBatch(false);

    const okCount = results.filter(r => r.ok).length;
    const failCount = results.filter(r => !r.ok).length;

    if (failCount === 0) {
      setSuccessMessage(`Successfully uploaded ${okCount} report${okCount === 1 ? '' : 's'}`);
    } else {
      setErrorMessage(`${okCount} of ${okCount + failCount} file(s) uploaded successfully. ${failCount} failed. Download the failure report or see details below.`);
    }

    return results;
  }, [runQueue]);

  const submit = async () => {
    setShowDialog(false);
    if (isUploadingBatch) return [];
    clearMessages();

    if (items.length === 0) return [];
    if (hasInvalid) {
      setErrorMessage('Fix invalid filenames before uploading.');
      if (firstInvalid) scrollToItem(firstInvalid.id);
      return [];
    }

    return runBatch(items);
  };

  const retryFailed = async () => {
    if (isUploadingBatch) return [];
    clearMessages();

    const requeued = items.map(item => (
      item.status === 'failed' ? { ...item, status: 'queued', progress: 0, error: null } : item
    ));
    setItems(requeued);

    // Only the re-queued rows carry status 'queued', so completed uploads are never re-sent.
    return runBatch(requeued);
  };

  const downloadFailureReport = () => {
    const problems = items.filter(i => i.status === 'failed' || i.status === 'invalid');
    if (problems.length === 0) return;

    const cell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['filename', 'size_kb', 'stage', 'error'],
      ...problems.map(i => [
        i.file.name,
        Math.round(i.file.size / 1024),
        i.status === 'invalid' ? 'filename validation' : 'upload',
        i.error || '',
      ]),
    ];
    const csv = rows.map(r => r.map(cell).join(',')).join('\r\n');

    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `bulk-upload-failures-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    getValidContentTypes().then(response => setValidContentTypes(response.data.join(',')));
  }, []);

  const hasFiles = items.length > 0;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item lg={6} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} elevation={0}>
              <FileDropzone
                limit={fileLimit}
                onDrop={onDrop}
                multiple
                accept={validContentTypes}
                description={''}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} elevation={0}>
          <Button
            className={classes.addReportButton}
            variant="contained"
            color="primary"
            onClick={() => setShowDialog(true)}
            disableElevation
            size="large"
            disabled={!hasFiles || hasInvalid || isUploadingBatch || uploadingOrSavingCount > 0 || queuedCount === 0}
            data-testid="add-report-button"
          >
            {isUploadingBatch ? 'Uploading...' : 'Start Upload'}
          </Button>

          <Button
            variant="outlined"
            disableElevation
            size="large"
            onClick={retryFailed}
            disabled={failedCount === 0 || isUploadingBatch || uploadingOrSavingCount > 0}
            style={{ marginLeft: 8 }}
            data-testid="retry-failed-button"
          >
            Retry failed
          </Button>

          <Button
            variant="outlined"
            disableElevation
            size="large"
            onClick={downloadFailureReport}
            disabled={failedCount + invalidCount === 0 || isUploadingBatch || uploadingOrSavingCount > 0}
            style={{ marginLeft: 8 }}
            data-testid="download-failures-button"
          >
            Download failure report
          </Button>

          <Button
            variant="text"
            disableElevation
            size="large"
            onClick={resetForm}
            disabled={isUploadingBatch || uploadingOrSavingCount > 0}
            style={{ marginLeft: 8 }}
          >
            Clear
          </Button>
        </Grid>

        {/* Summary */}
        {hasFiles && (
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary">
              {`${completedCount} completed • ${failedCount} failed • ${queuedCount} queued`}
              {uploadingOrSavingCount > 0 ? ` • ${uploadingOrSavingCount} in progress` : ''}
            </Typography>
          </Grid>
        )}

        {errorMessage && (
          <Grid item xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        )}
        {successMessage && (
          <Grid item xs={12}>
            <Alert severity="success">{successMessage}</Alert>
          </Grid>
        )}
        {hasInvalid && (
          <Grid item xs={12} style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <Alert
              severity="warning"
              action={(
                <>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => firstInvalid && scrollToItem(firstInvalid.id)}
                    disabled={!firstInvalid}
                  >
                    Jump to error
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setItems(prev => prev.filter(i => i.status !== 'invalid'))}
                  >
                    Remove invalid
                  </Button>
                </>
              )}
            >
              {`${invalidCount} file${invalidCount !== 1 ? 's have invalid names.' : ' has an invalid name.'}`}
            </Alert>
          </Grid>
        )}

        {/* Per-file list */}
        {hasFiles && (
          <Grid item xs={12}>
            {items.map(item => (
              <Paper
                key={item.id}
                ref={setRowRef(item.id)}
                variant="outlined"
                style={{
                  padding: 12,
                  marginBottom: 10,
                  border: item.status === 'invalid' ? '2px solid #f44336' : undefined,
                }}
              >
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <Typography variant="body2">{item.file.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {Math.round(item.file.size / 1024)}
                      {' '}
                      KB
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    {item.status === 'uploading' && (
                      <LinearProgress variant="determinate" value={item.progress || 0} />
                    )}
                    {item.status === 'saving' && <LinearProgress />}
                    {item.status === 'queued' && (
                      <Typography variant="caption" color="textSecondary">
                        Queued
                      </Typography>
                    )}
                    {item.status === 'validating' && (
                      <Typography variant="caption" color="textSecondary">
                        Validating filename…
                      </Typography>
                    )}
                    {item.status === 'invalid' && (
                      <Typography variant="caption" color="error">
                        Invalid:
                        {' '}
                        {item.error}
                      </Typography>
                    )}
                    {item.status === 'completed' && (
                      <Typography variant="caption" style={{ color: 'green' }}>
                        Completed
                      </Typography>
                    )}
                    {item.status === 'failed' && (
                      <Typography variant="caption" color="error">
                        Failed:
                        {' '}
                        {item.error}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={3} style={{ textAlign: 'right' }}>
                    <Button
                      size="small"
                      onClick={() => removeItem(item.id)}
                      disabled={item.status === 'uploading' || item.status === 'saving'}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Grid>
        )}
      </Grid>

      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        data-testid="warning-dialog"
      >
        <DialogTitle id="alert-dialog-title" disableTypography>
          Confirm bulk upload
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} className={classes.dialogContentGrid}>

            <Grid item xs={12}>
              <Typography>
                {`You are uploading ${items.length} file${items.length > 1 ? 's' : ''}`}
              </Typography>
            </Grid>

            {items.map(it => (
              <Grid item xs={12} key={it.id}>
                <Typography variant="body2">
                  •
                  {' '}
                  {it.file.name}
                  {it.status === 'invalid' ? ` (invalid: ${it.error})` : ''}
                </Typography>
              </Grid>
            ))}

            {hasInvalid && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  One or more filenames are invalid. Fix/remove invalid files before uploading.
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setShowDialog(false)}
            variant="contained"
            size="large"
            data-testid="dialog-cancel-button"
          >
            Cancel
          </Button>
          <Button
            disabled={hasInvalid || isUploadingBatch || uploadingOrSavingCount > 0 || queuedCount === 0}
            onClick={submit}
            variant="contained"
            color="primary"
            size="large"
            autoFocus
            data-testid="create-report-button"
          >
            Start Upload
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkReportUpload;
